import { createSelector, createSlice } from '@reduxjs/toolkit';

import isEqual from 'lodash/isEqual';
import { GrowthBookPayload } from '@growthbook/growthbook-react';

import { FeatureToggleEnum } from '@nocowanie/core/enums';

import { SliceNameEnum } from '@app/enums';
import {
    resetModalDataAction,
    setModalDataAction,
} from '@app/state/slices/common-ui-state.actions';

export type UiStateType = {
    ft: Partial<Record<FeatureToggleEnum, unknown>>;
    growthbookPayload: GrowthBookPayload | null;
    isMobileVersion: boolean;
    isSession: boolean | undefined;
    isPageDataLayerSent: boolean;
    modals: Record<string, unknown>;
};

export const initialState: UiStateType = {
    ft: {},
    growthbookPayload: null,
    isMobileVersion: false,
    isPageDataLayerSent: false,
    isSession: undefined,
    modals: {},
};

export const uiStateSlice = createSlice({
    name: SliceNameEnum.CommonUiState,
    initialState,
    reducers: {
        initPage: state => {
            state.isPageDataLayerSent = false;
            state.modals = {};
        },
        setFeatureToggle: (state, action) => {
            state.ft = {
                ...state.ft,
                ...action.payload,
            };
        },
        setGrowthbookPayload: (state, action: { payload: GrowthBookPayload }) => {
            state.growthbookPayload = action.payload;
        },
        setIsPageDataLayerSent: (state, action) => {
            state.isPageDataLayerSent = action.payload;
        },
        setIsSession: (state, action) => {
            state.isSession = action.payload;
        },
        setMobileVersion: (state, action) => {
            state.isMobileVersion = action.payload;
        },
    },
    extraReducers: builder => {
        builder.addCase(setModalDataAction, (state, { payload }) => {
            if (isEqual(payload.modalData, state.modals[payload.modalName])) {
                return;
            }

            state.modals[payload.modalName] = payload.modalData;
        });
        builder.addCase(resetModalDataAction, (state, { payload }) => {
            if (typeof state.modals[payload] === 'undefined') {
                return;
            }

            state.modals[payload] = undefined;
        });
    },
});

export const {
    initPage,
    setFeatureToggle,
    setGrowthbookPayload,
    setIsPageDataLayerSent,
    setIsSession,
    setMobileVersion,
} = uiStateSlice.actions;

const selectSelfState = (state: { [SliceNameEnum.CommonUiState]: UiStateType }): UiStateType => {
    return state[uiStateSlice.name] ?? initialState;
};

type CommonUiSliceState = ReturnType<typeof selectSelfState>;

export const actions = {
    resetModalDataAction,
    setFeatureToggle,
    setGrowthbookPayload,
    setMobileVersion,
    setModalDataAction,
};

export const selectors = {
    selectIsMobileVersion: createSelector(selectSelfState, state => state.isMobileVersion),
    selectGrowthbookPayload: createSelector(selectSelfState, state => state.growthbookPayload),
    selectFt: createSelector(selectSelfState, state => state.ft),
    selectFtActive: createSelector(
        [
            selectSelfState,
            (_state: CommonUiSliceState, featureToggle: FeatureToggleEnum) => featureToggle,
        ],
        (state: UiStateType, featureToggle: FeatureToggleEnum) => state.ft[featureToggle] ?? false,
    ),
    selectModalData: <T>(modalName: string) =>
        createSelector(selectSelfState, state => state.modals[modalName] as T),
    selectAnyModalActive: createSelector(selectSelfState, state =>
        Object.values(state.modals).some(Boolean),
    ),
    selectIsPageDataLayerSent: createSelector(selectSelfState, state => state.isPageDataLayerSent),
    selectIsSession: createSelector(selectSelfState, state => state.isSession),
    selectUIFtActive: <T = unknown>(featureToggle: FeatureToggleEnum) =>
        createSelector(selectSelfState, state => (state.ft[featureToggle] ?? false) as T),
};

export default uiStateSlice.reducer;
